import { useId } from "react";

import { Button } from "../../../../core/Button.js";
import { ChipRadioGroup } from "../../../../core/Chip.js";
import { EmptyState } from "../../../../core/EmptyState.js";
import { LoadMore } from "../../../../core/LoadMore.js";
import { Skeleton } from "../../../../core/Skeleton.js";
import { Text } from "../../../../core/Text.js";
import { ItemCard } from "../../../../domain/ItemCard.js";
import { ActionButton } from "../../../shared/ActionButton.js";
import { LoadError } from "../../../shared/LoadError.js";
import type { InventoryVaultContent } from "../../inventoryContent.js";

const GRID = "grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5 xl:grid-cols-6";

/**
 * The vault: the units in one status under chips that switch it, the line
 * that says how many can be sold with Select all, then the grid, or its loading, failed or empty
 * state in its place.
 */
export function InventoryVault({
  title,
  description,
  status,
  selectable,
  state,
  error,
  paging,
}: InventoryVaultContent) {
  const id = useId();
  const shown = state.status === "ready" ? state.units.length : 0;
  return (
    <section
      aria-labelledby={id}
      aria-busy={state.status === "loading" || undefined}
      className="flex flex-col gap-4"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="flex flex-col gap-1">
          <Text as="h2" id={id} variant="display-xl4" tone="primary">
            {title}
          </Text>
          <Text variant="body-b2" tone="secondary">
            {description}
          </Text>
        </div>
        <ChipRadioGroup
          label={status.label}
          value={status.value}
          onValueChange={status.onValueChange}
          options={status.options}
        />
      </div>
      {error && <LoadError message={error} />}
      {selectable && state.status === "ready" && (
        <div className="flex items-center justify-between gap-3">
          <Text variant="body-b2" tone="secondary">
            {selectable.summary}
          </Text>
          <Button variant="ghost" size="sm" onClick={selectable.toggleAll.onClick}>
            {selectable.toggleAll.label}
          </Button>
        </div>
      )}
      {state.status === "loading" && (
        <div aria-hidden className={GRID}>
          {["a", "b", "c", "d", "e", "f"].map((key) => (
            <Skeleton key={key} className="aspect-4/5 rounded-xl" />
          ))}
        </div>
      )}
      {state.status === "error" && <LoadError message={state.message} />}
      {state.status === "empty" && (
        <EmptyState
          title={state.message}
          action={state.action && <ActionButton action={state.action} variant="secondary" />}
        />
      )}
      {state.status === "ready" && (
        <>
          <ul className={GRID}>
            {state.units.map((unit) => (
              <li key={unit.id} className="flex">
                <ItemCard
                  variant="owned"
                  name={unit.name}
                  image={unit.imageUrl}
                  note={unit.note}
                  issue={unit.issue}
                  faded={unit.faded}
                  selection={unit.selection}
                />
              </li>
            ))}
          </ul>
          <LoadMore
            shown={shown}
            hasMore={paging.hasMore}
            loading={paging.loading}
            onLoadMore={paging.onLoadMore}
            label={paging.label}
            status={(n) => paging.status(n)}
          />
        </>
      )}
    </section>
  );
}
